/**
 * @file src/core/picking.js
 * Raycast chọn đối tượng. Nâng từ three-lessons/src/core/Picking.js.
 * BỔ SUNG: chỉ raycast trong danh sách đã đăng ký (nhanh hơn nhiều so với
 * intersectObjects(scene.children) mỗi frame), hover có throttle, dispose đúng.
 */

import * as THREE from 'three';

/**
 * @typedef {Object} Picker
 * @property {(objects: THREE.Object3D[]) => void} setTargets  Giới hạn phạm vi raycast
 * @property {(cb: (hit: THREE.Intersection | null) => void) => void} onClick
 * @property {(cb: (hit: THREE.Intersection | null) => void) => void} onHover
 * @property {() => THREE.Object3D | null} hovered
 * @property {() => void} update   Engine gọi mỗi frame (đã throttle)
 * @property {() => void} dispose
 */

/**
 * @param {{ camera: THREE.Camera, dom: HTMLElement, hoverIntervalMs?: number }} opts
 * @returns {Picker}
 */
export function createPicker(opts) {
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2(-999, -999);
  /** @type {THREE.Object3D[]} */
  let targets = [];
  /** @type {THREE.Object3D | null} */
  let hovered = null;
  /** @type {((h: THREE.Intersection | null) => void) | null} */
  let clickCb = null;
  /** @type {((h: THREE.Intersection | null) => void) | null} */
  let hoverCb = null;

  const hoverInterval = opts.hoverIntervalMs ?? 80;
  let lastHoverAt = 0;
  let pointerInside = false;

  const cast = () => {
    if (!targets.length || !pointerInside) return null;
    raycaster.setFromCamera(pointer, opts.camera);
    const hits = raycaster.intersectObjects(targets, true);
    return hits.length ? (hits[0] ?? null) : null;
  };

  /** @param {PointerEvent} e */
  const onMove = (e) => {
    const r = opts.dom.getBoundingClientRect();
    pointer.x = ((e.clientX - r.left) / r.width) * 2 - 1;
    pointer.y = -(((e.clientY - r.top) / r.height) * 2 - 1);
    pointerInside = true;
  };
  const onLeave = () => {
    pointerInside = false;
    if (hovered) {
      hovered = null;
      hoverCb?.(null);
    }
  };
  const onClick = () => {
    if (!clickCb) return;
    clickCb(cast());
  };

  opts.dom.addEventListener('pointermove', onMove);
  opts.dom.addEventListener('pointerleave', onLeave);
  opts.dom.addEventListener('click', onClick);

  return {
    setTargets(objects) {
      targets = objects ?? [];
    },
    onClick(cb) {
      clickCb = cb;
    },
    onHover(cb) {
      hoverCb = cb;
    },
    hovered: () => hovered,
    update() {
      if (!hoverCb) return;
      const now = performance.now();
      if (now - lastHoverAt < hoverInterval) return;
      lastHoverAt = now;
      const hit = cast();
      const obj = hit?.object ?? null;
      if (obj !== hovered) {
        hovered = obj;
        hoverCb(hit);
      }
    },
    dispose() {
      opts.dom.removeEventListener('pointermove', onMove);
      opts.dom.removeEventListener('pointerleave', onLeave);
      opts.dom.removeEventListener('click', onClick);
      targets = [];
      clickCb = null;
      hoverCb = null;
      hovered = null;
    },
  };
}
